/* eslint-disable class-methods-use-this */
import type Dagre from '@dagrejs/dagre';
import type {
	ApiGetLineageFlowQueryParams,
	ApiGetMultipleLineageFlowQueryParams,
	Filter,
	LineageFlowChild,
	LineageFlowCreationQuery,
	LineageFlowMonitor,
	LineageFlowTest,
} from '@repo/api-codegen';
import {
	fetchApiCreateLineage,
	fetchApiDeleteLineage,
	fetchApiGetLineageFlow,
	fetchApiGetLineageFlowChildren,
	fetchApiGetLineageFlowImpactedChildren,
	fetchApiGetMultipleLineageFlow,
} from '@repo/api-codegen';
import { fetchApiGetLineageFlowMetadataV2 } from '@repo/api-codegen/v2';

import { trackEvent } from '@repo/common/analytics';

// eslint-disable-next-line no-restricted-imports
import { getColor } from '@repo/theme/utils';
import type { QueryClient } from '@tanstack/react-query';
import type { EdgeChange, NodeChange, ReactFlowInstance } from '@xyflow/react';
import {
	applyEdgeChanges,
	applyNodeChanges,
	getNodesBounds,
	getViewportForBounds,
	MarkerType,
	Position,
} from '@xyflow/react';
import { toPng } from 'html-to-image';
import { debounce } from 'lodash-es';
import { makeAutoObservable, runInAction } from 'mobx';
import type { MouseEvent as ReactMouseEvent } from 'react';
import {
	isEntityEdge,
	type LineageFlowEdge,
} from '../components/EntityEdge/EntityEdge.types';
import { ENTITY_NODE_WIDTH } from '../components/EntityNode/EntityNode.constants';
import type { LineageFlowNode } from '../components/EntityNode/EntityNode.types';
import { isLineageFlowNode } from '../components/EntityNode/EntityNode.types';
import { DAGRE_RANK_SEP } from '../components/LineageFlow/LineageFlow.constants';
import {
	TEMPORARY_NODE_HEIGHT,
	TEMPORARY_NODE_ID_PREFIX,
	TEMPORARY_NODE_WIDTH,
} from '../components/TemporaryNode/TemporaryNode.constants';
import {
	isLineageFlowTemporaryNode,
	type LineageFlowTemporaryNode,
} from '../components/TemporaryNode/TemporaryNode.types';
import {
	LINEAGE_FLOW_DEBOUNCE_TIME,
	LINEAGE_FLOW_DEPTH_LOCAL_STORAGE_KEY,
} from './LineageFlowStore.constants';
import type { LineageFlowDimensions } from './LineageFlowStore.types';
import {
	calculateEntityNodeChildrenHeight,
	calculateEntityNodeHeight,
	calculateEntityNodeHighlightedChildrenHeight,
	calculateLayout,
	determineParentChildRelationship,
	fetchQuery,
	isNodeBelow,
} from './LineageFlowStore.utils';

class LineageFlowStore {
	// REACT QUERY CLIENT
	queryClient: QueryClient;

	// ACCESS PROPERTIES
	createAccess: boolean = false;

	// CORE PROPERTIES FOR REACTFLOW, LAYOUT AND POSITIONING
	reactFlowInstance:
		| ReactFlowInstance<
				LineageFlowNode | LineageFlowTemporaryNode,
				LineageFlowEdge
		  >
		| undefined;

	graph: Dagre.graphlib.Graph | undefined;
	rankMap: Map<number, string[]> | undefined;
	dimensions: LineageFlowDimensions = { width: 0, height: 0 };

	// NODE PROPERTIES
	rootID: string;
	nodes: (LineageFlowNode | LineageFlowTemporaryNode)[] = [];
	temporaryNodeIds: Set<string> = new Set();

	// FOCUSED NODE PROPERTIES
	focusedNodeID: string | undefined;

	// NODE CHILDREN PROPERTIES
	fetchedChildrenIDs: Set<string> = new Set();
	expandedNodeIDs: Set<string> = new Set();

	// EDGE PROPERTIES
	edges: LineageFlowEdge[] = [];

	// INITIAL STATE FOR REINITIALIZATION
	initialNodes: (LineageFlowNode | LineageFlowTemporaryNode)[] = [];
	initialEdges: LineageFlowEdge[] = [];

	// IMPACTED CHILDREN PROPERTIES
	impactChildID: string | undefined;
	isFetchingImpactedChildren: boolean = false;

	// PARENT CHILDREN PROPERTIES
	parentChildMap: Map<string, LineageFlowChild[]> = new Map();
	highlightedParentChildMap: Map<string, LineageFlowChild[]> = new Map();

	// NODE METADATA PROPERTIES
	tests: Map<string, LineageFlowTest[]> = new Map();
	monitors: Map<string, LineageFlowMonitor[]> = new Map();
	creationQueries: Map<string, LineageFlowCreationQuery[]> = new Map();

	// FILTER PROPERTIES
	filter: Filter | undefined;
	upstreamDepth: number = 1;
	downstreamDepth: number = 1;

	// UI PROPERTIES
	isLoading: boolean = false;

	// ERROR HANDLING PROPERTIES
	handleError?: (error: string) => void;

	constructor(
		rootID: string,
		queryClient: QueryClient,
		handleError?: (error: string) => void
	) {
		this.rootID = rootID;
		this.queryClient = queryClient;
		this.handleError = handleError;

		makeAutoObservable(this, {
			setDimensions: false,
		});
	}

	private setDimensionsInternal = (
		observerRect: Omit<DOMRectReadOnly, 'toJSON'>
	) => {
		runInAction(() => {
			this.dimensions = {
				width: observerRect.width,
				height: observerRect.height,
			};
		});
	};

	private updateGraphState = ({
		updatedGraph,
		updatedRankMap,
		updatedNodes,
	}: {
		updatedGraph: Dagre.graphlib.Graph;
		updatedRankMap: Map<number, string[]>;
		updatedNodes: (LineageFlowNode | LineageFlowTemporaryNode)[];
	}) => {
		runInAction(() => {
			this.graph = updatedGraph;
			this.rankMap = updatedRankMap;
			this.nodes = updatedNodes;
		});
	};

	private async fetchAndMergeMetadata(nodeIds: string[]) {
		const { tests, monitors, creation_queries } =
			await fetchApiGetLineageFlowMetadataV2({
				body: { ids: nodeIds.join(',') },
			});

		runInAction(() => {
			Object.entries(creation_queries).forEach(([key, value]) =>
				this.creationQueries.set(key, value)
			);
			Object.entries(monitors).forEach(([key, value]) =>
				this.monitors.set(key, value)
			);
			Object.entries(tests).forEach(([key, value]) =>
				this.tests.set(key, value)
			);
		});
	}

	private updateEdgeMarkers(edges: LineageFlowEdge[]): LineageFlowEdge[] {
		return edges.map((edge) => ({
			...edge,
			markerEnd:
				edge.markerEnd && typeof edge.markerEnd === 'object'
					? {
							...edge.markerEnd,
							color: getColor('border/primary/default'),
						}
					: {
							type: MarkerType.Arrow,
							width: 24,
							height: 24,
							color: getColor('border/primary/default'),
						},
		}));
	}

	private buildQueryParams(
		id: string,
		direction?: 'UPSTREAM' | 'DOWNSTREAM'
	): ApiGetLineageFlowQueryParams {
		const queryParams: ApiGetLineageFlowQueryParams = {
			id,
			root_node_id: this.rootID,
			upstream_depth: this.upstreamDepth,
			downstream_depth: this.downstreamDepth,
		};

		if (direction) {
			queryParams.direction = direction;
		}

		if (this.filter) {
			queryParams.filter = JSON.stringify(this.filter);
		}

		return queryParams;
	}

	private updateEdgeHover(edgeId: string, hovered: boolean) {
		runInAction(() => {
			this.edges = this.edges.map((e) => {
				if (e.id === edgeId && e.data) {
					return {
						...e,
						data: {
							...e.data,
							hovered,
						},
					};
				}
				return e;
			});
		});
	}

	private determineNodeToDelete(
		sourceNode: LineageFlowNode | LineageFlowTemporaryNode | undefined,
		targetNode: LineageFlowNode | LineageFlowTemporaryNode | undefined
	): {
		nodeToDelete?: LineageFlowNode;
		direction?: 'UPSTREAM' | 'DOWNSTREAM';
	} {
		if (!sourceNode || !targetNode) {
			return {};
		}

		const sourceDirection = isLineageFlowNode(sourceNode)
			? sourceNode.data.direction
			: undefined;
		const targetDirection = isLineageFlowNode(targetNode)
			? targetNode.data.direction
			: undefined;

		if (sourceDirection === 'UPSTREAM' && !targetDirection) {
			return {
				nodeToDelete: sourceNode as LineageFlowNode,
				direction: 'UPSTREAM',
			};
		}
		if (!sourceDirection && targetDirection === 'DOWNSTREAM') {
			return {
				nodeToDelete: targetNode as LineageFlowNode,
				direction: 'DOWNSTREAM',
			};
		}
		if (sourceDirection === 'UPSTREAM' && targetDirection === 'UPSTREAM') {
			return {
				nodeToDelete: sourceNode as LineageFlowNode,
				direction: 'UPSTREAM',
			};
		}
		if (sourceDirection === 'DOWNSTREAM' && targetDirection === 'DOWNSTREAM') {
			return {
				nodeToDelete: targetNode as LineageFlowNode,
				direction: 'DOWNSTREAM',
			};
		}

		return {};
	}

	private setLoadingState(loading: boolean) {
		runInAction(() => {
			this.isLoading = loading;
		});
	}

	setDimensions = debounce(
		this.setDimensionsInternal,
		LINEAGE_FLOW_DEBOUNCE_TIME
	);

	resetToEmptyState = () => {
		runInAction(() => {
			// Reset all node and edge properties
			this.nodes = [];
			this.edges = [];
			this.initialNodes = [];
			this.initialEdges = [];
			this.temporaryNodeIds.clear();

			// Reset children properties
			this.impactChildID = undefined;
			this.highlightedParentChildMap.clear();
			this.expandedNodeIDs.clear();
			this.fetchedChildrenIDs.clear();
			this.parentChildMap.clear();

			// Reset metadata
			this.tests.clear();
			this.monitors.clear();
			this.creationQueries.clear();

			// Reset UI state
			this.isLoading = false;
			this.focusedNodeID = undefined;

			// Reset graph state
			this.graph = undefined;
			this.rankMap = undefined;
		});
	};

	// UTIL ACTION TO RESET LINEAGE FLOW NODES AND EDGES
	resetNodesAndEdges = async (options?: { fitView?: boolean }) => {
		this.setLoadingState(true);

		const nodeToFetchID = this.focusedNodeID ?? this.rootID;
		const queryParams = this.buildQueryParams(nodeToFetchID);

		const {
			nodes: fetchedNodes,
			edges: fetchedEdges,
			error,
		} = await fetchQuery(
			this.queryClient,
			{
				path: '/lineage/flow/',
				operationId: 'apiGetLineageFlow',
				variables: { queryParams },
			},
			() => fetchApiGetLineageFlow({ queryParams })
		);

		if (error) {
			this.handleError?.(error);
			this.setLoadingState(false);
			return;
		}

		const { updatedGraph, updatedRankMap, updatedNodes } =
			await calculateLayout({
				nodes: fetchedNodes,
				edges: fetchedEdges as LineageFlowEdge[],
			});

		const fetchedEdgesWithMarkerEndUpdated = this.updateEdgeMarkers(
			fetchedEdges as LineageFlowEdge[]
		);

		runInAction(() => {
			this.graph = updatedGraph;
			this.rankMap = updatedRankMap;
			this.nodes = updatedNodes as (
				| LineageFlowNode
				| LineageFlowTemporaryNode
			)[];
			this.edges = fetchedEdgesWithMarkerEndUpdated;
			this.impactChildID = undefined;
			this.highlightedParentChildMap.clear();
			this.expandedNodeIDs.clear();
			this.initialNodes = updatedNodes as (
				| LineageFlowNode
				| LineageFlowTemporaryNode
			)[];
			this.initialEdges = fetchedEdgesWithMarkerEndUpdated;
		});

		if (options?.fitView) {
			// fitView won't work until layout is calculated
			// Double rAF ensures fitView is called after the next layout calculation
			// Flush any pending debounced dimension updates first to avoid race condition
			// where fitView runs before dimensions are set
			this.setDimensions.flush();
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					// Guard against fitView when dimensions are not yet set (race condition)
					// This can happen if resize observer hasn't fired or debounce hasn't resolved
					if (this.dimensions.width > 0 && this.dimensions.height > 0) {
						this.reactFlowInstance?.fitView();
					} else {
						// Retry after a short delay to allow dimensions to settle
						setTimeout(() => {
							this.reactFlowInstance?.fitView();
						}, 100);
					}
				});
			});
		}

		this.setLoadingState(false);
		await this.fetchAndMergeMetadata(fetchedNodes.map((node) => node.id));
	};

	// UTIL ACTION TO RESET IMPACTED CHILDREN STATE
	resetImpactedChildrenState = async () => {
		const entityEdges = this.edges.filter(isEntityEdge);

		const { updatedGraph, updatedRankMap, updatedNodes } =
			await calculateLayout({
				nodes: [...this.nodes],
				edges: entityEdges,
			});

		runInAction(() => {
			this.highlightedParentChildMap.clear();
			this.expandedNodeIDs.clear();
			this.edges = entityEdges;

			this.updateGraphState({
				updatedGraph,
				updatedRankMap,
				updatedNodes: updatedNodes as (
					| LineageFlowNode
					| LineageFlowTemporaryNode
				)[],
			});
		});
	};

	onInitReactFlow = async ({
		reactFlowInstance,
		createAccess,
		filter,
	}: {
		reactFlowInstance: ReactFlowInstance<
			LineageFlowNode | LineageFlowTemporaryNode,
			LineageFlowEdge
		>;
		createAccess: boolean;
		filter?: Filter;
	}) => {
		// If we already have nodes/edges loaded (store persisted across tab switches),
		// just update the instance and filter/access without resetting
		// This prevents clearing and reloading data when switching tabs
		if (this.nodes.length > 0 && this.edges.length > 0) {
			runInAction(() => {
				this.reactFlowInstance = reactFlowInstance;
				if (filter) {
					this.filter = filter;
				}
				this.createAccess = createAccess;
			});

			// Ensure fitView is called even when reusing cached nodes
			// This fixes race condition where graph appears off-screen after tab switch
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					this.reactFlowInstance?.fitView();
				});
			});

			return;
		}

		const startTime = performance.now();

		if (filter) {
			this.filter = filter;
		}
		this.createAccess = createAccess;

		const localStorageUpstreamDepth = localStorage.getItem(
			`${LINEAGE_FLOW_DEPTH_LOCAL_STORAGE_KEY}_UPSTREAM`
		);
		const localStorageDownstreamDepth = localStorage.getItem(
			`${LINEAGE_FLOW_DEPTH_LOCAL_STORAGE_KEY}_DOWNSTREAM`
		);
		this.upstreamDepth = localStorageUpstreamDepth
			? parseInt(localStorageUpstreamDepth, 10)
			: 1;
		this.downstreamDepth = localStorageDownstreamDepth
			? parseInt(localStorageDownstreamDepth, 10)
			: 1;

		this.reactFlowInstance = reactFlowInstance;

		await this.resetNodesAndEdges({ fitView: true });

		const endTime = performance.now();
		trackEvent('lineage/ui/lineage_flow/initialize', {
			// ENTITY METADATA
			entity_id: this.rootID,
			filters: JSON.stringify(filter),
			// GRAPH METADATA
			number_of_nodes: `${this.nodes.length}`,
			number_of_edges: `${this.edges.length}`,
			duration: `${endTime - startTime}`,
		});
	};

	onFilterChange = (filter?: Filter) => {
		if (this.reactFlowInstance && filter) {
			const isFilterValueChanged =
				JSON.stringify(this.filter) !== JSON.stringify(filter);

			this.filter = filter;

			if (isFilterValueChanged) {
				this.resetNodesAndEdges({ fitView: true });
			}
		}
	};

	// ACTION HANDLER TO FETCH LINEAGE DATA
	onFetchLineage = async ({
		id,
		direction,
		invalidate = false,
	}: {
		id: string;
		direction: 'UPSTREAM' | 'DOWNSTREAM';
		invalidate?: boolean;
	}) => {
		if (!this.graph || !this.reactFlowInstance?.getNode(id)) return;

		this.setLoadingState(true);

		const startTime = performance.now();
		const queryParams = this.buildQueryParams(id, direction);

		const {
			nodes: fetchedNodes,
			edges: fetchedEdges,
			error,
		} = await fetchQuery(
			this.queryClient,
			{
				path: '/lineage/flow/',
				operationId: 'apiGetLineageFlow',
				variables: { queryParams },
			},
			() => fetchApiGetLineageFlow({ queryParams }),
			invalidate
		);

		if (error) {
			this.handleError?.(error);
			this.setLoadingState(false);
			return;
		}
		const { updatedGraph, updatedRankMap, updatedNodes } =
			await calculateLayout({
				nodes: [...this.nodes, ...fetchedNodes],
				edges: [...this.edges, ...(fetchedEdges as LineageFlowEdge[])],
				fetchedID: id,
				fetchedDirection: direction,
				highlightedParentChildMap: this.highlightedParentChildMap,
			});

		runInAction(() => {
			this.updateGraphState({
				updatedGraph,
				updatedRankMap,
				updatedNodes: updatedNodes as (
					| LineageFlowNode
					| LineageFlowTemporaryNode
				)[],
			});
			this.edges = [...this.edges, ...(fetchedEdges as LineageFlowEdge[])];
		});

		await this.fetchAndMergeMetadata(fetchedNodes.map((node) => node.id));

		const endTime = performance.now();
		trackEvent('lineage/ui/lineage_flow/fetch_lineage', {
			entity_id: id,
			direction,
			filters: JSON.stringify(this.filter),
			duration: `${endTime - startTime}`,
		});

		this.setLoadingState(false);
	};

	// ACTION HANDLER TO FETCH CHILDREN DATA
	onFetchChildren = async ({ parentID }: { parentID: string }) => {
		if (this.parentChildMap.has(parentID)) return;

		const { children } = await fetchQuery(
			this.queryClient,
			{
				path: '/lineage/flow/children/',
				operationId: 'apiGetLineageFlowChildren',
				variables: { queryParams: { id: parentID } },
			},
			() => fetchApiGetLineageFlowChildren({ queryParams: { id: parentID } })
		);

		runInAction(() => {
			this.parentChildMap.set(parentID, children);
			this.fetchedChildrenIDs.add(parentID);
		});
	};

	// ACTION HANDLER TO FETCH IMPACTED CHILDREN DATA
	onFetchImpactedChildren = async ({
		impactChildID,
	}: {
		impactChildID: string;
	}) => {
		// Disallow concurrent calls (i.e. spam-clicking), this prevents inconsistent state (e.g. orphaned edges onscreen)
		if (this.isFetchingImpactedChildren) {
			return;
		}

		if (this.impactChildID === impactChildID) {
			this.impactChildID = undefined;
			this.resetImpactedChildrenState();
			return;
		}

		if (this.impactChildID && this.impactChildID !== impactChildID) {
			this.resetImpactedChildrenState();
		}

		const startTime = performance.now();

		this.isFetchingImpactedChildren = true;

		try {
			const { children: impactedChildren } = await fetchQuery(
				this.queryClient,
				{
					path: '/lineage/flow/impacted-children/',
					operationId: 'apiGetLineageFlowImpactedChildren',
					variables: { queryParams: { id: impactChildID } },
				},
				() =>
					fetchApiGetLineageFlowImpactedChildren({
						queryParams: { id: impactChildID },
					})
			);

			const {
				newHighlightedParentChildMap,
				newExpandedNodeIDs,
				newChildEdges,
			} = determineParentChildRelationship({
				children: impactedChildren,
			});

			const { updatedGraph, updatedRankMap, updatedNodes } =
				await calculateLayout({
					nodes: [...this.nodes],
					edges: this.edges.filter(isEntityEdge),
					highlightedParentChildMap: newHighlightedParentChildMap,
				});

			runInAction(() => {
				this.impactChildID = impactChildID;

				this.updateGraphState({
					updatedGraph,
					updatedRankMap,
					updatedNodes: updatedNodes as (
						| LineageFlowNode
						| LineageFlowTemporaryNode
					)[],
				});
				this.edges = [...this.edges, ...newChildEdges];
				this.highlightedParentChildMap = newHighlightedParentChildMap;
				this.expandedNodeIDs = newExpandedNodeIDs;
			});

			const endTime = performance.now();
			trackEvent('lineage/ui/lineage_flow/fetch_impacted_children', {
				entity_id: impactChildID,
				duration: `${endTime - startTime}`,
			});
		} finally {
			runInAction(() => {
				this.isFetchingImpactedChildren = false;
			});
		}
	};

	// ACTION HANDLER TO UPDATE POSITIONS AFTER NODE CHILDREN TOGGLED
	onUpdatePositionsAfterNodeChildrenToggled = ({
		toggledNodeID,
		expanded,
		fetched,
	}: {
		toggledNodeID: string;
		expanded: boolean;
		fetched: boolean;
	}) => {
		const toggledNode = this.reactFlowInstance?.getNode(toggledNodeID);
		const toggledDagreNode = this.graph?.node(toggledNodeID);

		if (
			!toggledNode ||
			!toggledDagreNode ||
			toggledDagreNode.rank === undefined ||
			toggledDagreNode.rank === null
		)
			return;

		const nodesInRank = this.rankMap?.get(toggledDagreNode.rank);
		if (!nodesInRank) return;

		let heightChange: number = TEMPORARY_NODE_HEIGHT;

		if (isLineageFlowNode(toggledNode)) {
			heightChange = this.highlightedParentChildMap.has(toggledNodeID)
				? calculateEntityNodeHighlightedChildrenHeight({
						count:
							this.highlightedParentChildMap.get(toggledNodeID)?.length ?? 0,
					})
				: calculateEntityNodeChildrenHeight({
						count: toggledNode.data.children_count ?? 0,
					});
		}

		const multiplier = expanded ? 1 : -1;

		runInAction(() => {
			// Update expanded state
			if (expanded) {
				this.expandedNodeIDs.add(toggledNodeID);
			} else {
				this.expandedNodeIDs.delete(toggledNodeID);
			}

			// Update node positions
			this.nodes = this.nodes.map((node) => {
				if (
					nodesInRank.includes(node.id) &&
					isNodeBelow({
						positionA: node.position,
						positionB: toggledNode.position,
					})
				) {
					return {
						...node,
						position: {
							...node.position,
							y: node.position.y + heightChange * multiplier,
						},
					};
				}

				return node;
			});
		});

		// Fetch children outside of runInAction since it's an async operation
		if (!fetched) {
			this.onFetchChildren({ parentID: toggledNodeID });
		}
	};

	// ACTION HANDLER TO ADD TEMPORARY NODE
	onAddTemporaryNode = ({
		nodeId,
		position,
	}: {
		nodeId: string;
		position: Position;
	}) => {
		if (this.temporaryNodeIds.size >= 1) return;

		const connectedNode = this.reactFlowInstance?.getNode(nodeId);
		const connectedDagreNode = this.graph?.node(nodeId);

		if (
			!connectedNode ||
			!connectedDagreNode ||
			connectedDagreNode.rank === undefined
		)
			return;

		const temporaryNodeId = `${TEMPORARY_NODE_ID_PREFIX}-${nodeId}-${position}`;
		if (this.temporaryNodeIds.has(temporaryNodeId)) return;

		const adjacentRank =
			position === Position.Left
				? connectedDagreNode.rank - 2
				: connectedDagreNode.rank + 2;
		const nodesInAdjacentRank = this.rankMap?.get(adjacentRank);

		const nodesUpdatedInRank = this.nodes.map((node) => {
			const isInRank = nodesInAdjacentRank?.includes(node.id);
			const isBelow = isNodeBelow({
				positionA: node.position,
				positionB: connectedNode.position,
			});

			const multiplier = isBelow ? 1 : -1;
			let heightChange = TEMPORARY_NODE_HEIGHT * multiplier;

			if (isInRank) {
				return {
					...node,
					position: {
						...node.position,
						y: node.position.y + heightChange,
					},
				};
			}

			return node;
		});

		const temporaryNode: LineageFlowTemporaryNode = {
			id: temporaryNodeId,
			type: 'TEMPORARY',
			position: {
				x:
					position === Position.Left
						? connectedNode.position.x - DAGRE_RANK_SEP - TEMPORARY_NODE_WIDTH
						: connectedNode.position.x + DAGRE_RANK_SEP + TEMPORARY_NODE_WIDTH,
				y: connectedNode.position.y,
			},
			data: {
				nodeId,
				position,
			},
		};

		const temporaryEdge: LineageFlowEdge =
			position === Position.Left
				? {
						id: `${temporaryNodeId}-${nodeId}`,
						source: temporaryNodeId,
						target: nodeId,
						type: 'ENTITY',
						sourceHandle: 'TEMP_SOURCE',
						targetHandle: 'MAIN_TARGET',
						data: {
							hovered: false,
						},
						markerEnd: {
							type: MarkerType.Arrow,
							width: 24,
							height: 24,
							color: getColor('border/primary/default'),
						},
						animated: true,
						deletable: false,
					}
				: {
						id: `${nodeId}-${temporaryNodeId}`,
						source: nodeId,
						target: temporaryNodeId,
						type: 'ENTITY',
						sourceHandle: 'MAIN_SOURCE',
						targetHandle: 'TEMP_TARGET',
						data: {
							hovered: false,
						},
						markerEnd: {
							type: MarkerType.Arrow,
							width: 24,
							height: 24,
							color: getColor('border/primary/default'),
						},
						animated: true,
						deletable: false,
					};

		runInAction(() => {
			this.nodes = [...nodesUpdatedInRank, temporaryNode];
			this.edges = [...this.edges, temporaryEdge];
			this.temporaryNodeIds.add(temporaryNodeId);
		});
	};

	// ACTION HANDLER TO SAVE TEMPORARY NODE
	onSaveTemporaryNode = async ({
		temporaryNodeId,
		entityId,
	}: {
		temporaryNodeId: string;
		entityId: string;
	}) => {
		if (!this.reactFlowInstance) return;
		const temporaryNode = this.reactFlowInstance.getNode(temporaryNodeId);

		if (!temporaryNode || !isLineageFlowTemporaryNode(temporaryNode)) return;

		const fromEntityId =
			temporaryNode.data.position === Position.Left
				? entityId
				: temporaryNode.data.nodeId;
		const toEntityId =
			temporaryNode.data.position === Position.Left
				? temporaryNode.data.nodeId
				: entityId;

		await fetchApiCreateLineage({
			body: {
				from_entity: fromEntityId,
				to_entity: toEntityId,
				direction: 'DOWNSTREAM',
			},
		});

		runInAction(() => {
			this.nodes = this.nodes.filter((node) => node.id !== temporaryNodeId);
			this.edges = this.edges.filter(
				(edge) =>
					edge.source !== temporaryNodeId && edge.target !== temporaryNodeId
			);
			this.temporaryNodeIds.delete(temporaryNodeId);
		});

		// Fetch lineage outside of runInAction since it's an async operation
		await this.onFetchLineage({
			id: temporaryNode.data.nodeId,
			direction:
				temporaryNode.data.position === Position.Left
					? 'UPSTREAM'
					: 'DOWNSTREAM',
			invalidate: true,
		});
	};

	// ACTION HANDLER TO REMOVE TEMPORARY NODE
	onRemoveTemporaryNode = async ({
		temporaryNodeId,
	}: {
		temporaryNodeId: string;
	}) => {
		const currentNodes = this.nodes.filter(
			(node) => node.id !== temporaryNodeId
		);
		const currentEdges = this.edges.filter(
			(edge) =>
				edge.source !== temporaryNodeId && edge.target !== temporaryNodeId
		);

		const { updatedGraph, updatedRankMap, updatedNodes } =
			await calculateLayout({
				nodes: currentNodes,
				edges: currentEdges,
			});

		runInAction(() => {
			this.temporaryNodeIds.delete(temporaryNodeId);
			this.updateGraphState({
				updatedGraph,
				updatedRankMap,
				updatedNodes: updatedNodes as (
					| LineageFlowNode
					| LineageFlowTemporaryNode
				)[],
			});
			this.edges = currentEdges;
		});
	};

	// ACTION HANDLER TO EXPAND ALL NODES
	onExpandAllNodes = async () => {
		const nonFetchedDownstreamNodeIds = this.nodes
			.filter(
				(node) =>
					isLineageFlowNode(node) &&
					node.data.direction === 'DOWNSTREAM' &&
					node.data.downstream_count > 0 &&
					node.data.downstream_fetched === false
			)
			.map((node) => node.id);

		const nonFetchedUpstreamNodeIds = this.nodes
			.filter(
				(node) =>
					isLineageFlowNode(node) &&
					node.data.direction === 'UPSTREAM' &&
					node.data.upstream_count > 0 &&
					node.data.upstream_fetched === false
			)
			.map((node) => node.id);

		const queryParams: ApiGetMultipleLineageFlowQueryParams = {
			root_node_id: this.rootID,
			upstream_ids: nonFetchedUpstreamNodeIds.join(','),
			downstream_ids: nonFetchedDownstreamNodeIds.join(','),
			upstream_depth: this.upstreamDepth,
			downstream_depth: this.downstreamDepth,
			...(this.filter && { filter: JSON.stringify(this.filter) }),
		};

		const {
			nodes: fetchedNodes,
			edges: fetchedEdges,
			error,
		} = await fetchQuery(
			this.queryClient,
			{
				path: '/lineage/flow/multiple/',
				operationId: 'apiGetMultipleLineageFlow',
				variables: { queryParams },
			},
			() => fetchApiGetMultipleLineageFlow({ queryParams })
		);

		if (error) {
			this.handleError?.(error);
			this.setLoadingState(false);
			return;
		}

		const { updatedGraph, updatedRankMap, updatedNodes } =
			await calculateLayout({
				nodes: [
					...this.nodes.map((node) => {
						if (
							isLineageFlowNode(node) &&
							nonFetchedUpstreamNodeIds.includes(node.data.id)
						)
							return {
								...node,
								data: {
									...node.data,
									upstream_fetched: true,
								},
							};

						if (
							isLineageFlowNode(node) &&
							nonFetchedDownstreamNodeIds.includes(node.data.id)
						)
							return {
								...node,
								data: {
									...node.data,
									downstream_fetched: true,
								},
							};
						return node;
					}),
					...fetchedNodes,
				],
				edges: [...this.edges, ...(fetchedEdges as LineageFlowEdge[])],
			});

		runInAction(() => {
			this.updateGraphState({
				updatedGraph,
				updatedRankMap,
				updatedNodes: updatedNodes as (
					| LineageFlowNode
					| LineageFlowTemporaryNode
				)[],
			});
			this.edges = [...this.edges, ...(fetchedEdges as LineageFlowEdge[])];
		});

		await this.fetchAndMergeMetadata(fetchedNodes.map((node) => node.id));
	};

	// ACTION HANDLER TO EXPORT TO PNG
	onExportToPNG = async () => {
		if (!this.reactFlowInstance) return;

		const bounds = getNodesBounds(this.reactFlowInstance.getNodes());
		const transform = getViewportForBounds(
			bounds,
			this.dimensions.width,
			this.dimensions.height,
			0.5,
			2,
			0
		);

		const viewportElement = document.querySelector(
			'.react-flow__viewport'
		) as HTMLElement | null;

		if (!viewportElement) return;

		const href = await toPng(viewportElement, {
			backgroundColor: getColor('surface/primary/default'),
			width: this.dimensions.width,
			height: this.dimensions.height,
			style: {
				width: `${this.dimensions.width}px`,
				height: `${this.dimensions.height}px`,
				transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.zoom})`,
			},
		});

		const aTag = document.createElement('a');
		aTag.setAttribute('download', 'Secoda Lineage.png');
		aTag.setAttribute('href', href);
		aTag.click();
	};

	// ACTION HANDLER TO NAVIGATE TO NODE
	onNavigateToNode = ({
		nodeID,
		zoom = 1,
		duration = 1000,
	}: {
		nodeID: string;
		zoom?: number;
		duration?: number;
	}) => {
		if (!this.reactFlowInstance) return;

		const node = this.reactFlowInstance.getNode(nodeID);
		if (!node) return;

		this.reactFlowInstance.setCenter(
			node.position.x + ENTITY_NODE_WIDTH / 2,
			node.position.y +
				(node.measured?.height ?? calculateEntityNodeHeight(node)) / 2,
			{ zoom, duration }
		);
	};

	// ACTION HANDLER TO REINITIALIZE LINEAGE
	onReinitialize = () => {
		runInAction(() => {
			this.nodes = this.initialNodes;
			this.edges = this.initialEdges;
			this.temporaryNodeIds.clear();

			this.impactChildID = undefined;
			this.highlightedParentChildMap.clear();
			this.expandedNodeIDs.clear();

			this.reactFlowInstance?.fitView();
		});
	};

	// ACTION HANDLER TO SET DEPTH
	onSetDepth = ({
		direction,
		depth,
	}: {
		direction: 'UPSTREAM' | 'DOWNSTREAM';
		depth: number;
	}) => {
		if (direction === 'UPSTREAM') {
			this.upstreamDepth = depth;
		} else {
			this.downstreamDepth = depth;
		}

		localStorage.setItem(
			`${LINEAGE_FLOW_DEPTH_LOCAL_STORAGE_KEY}_${direction}`,
			depth.toString()
		);

		this.resetNodesAndEdges();
	};

	// HELPER METHODS TO GET CONNECTED NODES
	getIncomers = (
		node: LineageFlowNode | LineageFlowTemporaryNode,
		nodes: (LineageFlowNode | LineageFlowTemporaryNode)[],
		edges: LineageFlowEdge[]
	): (LineageFlowNode | LineageFlowTemporaryNode)[] => {
		const incomingEdges = edges.filter((edge) => edge.target === node.id);
		return incomingEdges
			.map((edge) => nodes.find((n) => n.id === edge.source))
			.filter((n): n is LineageFlowNode | LineageFlowTemporaryNode =>
				Boolean(n)
			);
	};

	getOutgoers = (
		node: LineageFlowNode | LineageFlowTemporaryNode,
		nodes: (LineageFlowNode | LineageFlowTemporaryNode)[],
		edges: LineageFlowEdge[]
	): (LineageFlowNode | LineageFlowTemporaryNode)[] => {
		const outgoingEdges = edges.filter((edge) => edge.source === node.id);
		return outgoingEdges
			.map((edge) => nodes.find((n) => n.id === edge.target))
			.filter((n): n is LineageFlowNode | LineageFlowTemporaryNode =>
				Boolean(n)
			);
	};

	// HELPER METHOD TO GET DELETE IMPACTED NODE IDS RECURSIVELY
	getDeleteImpactedNodeIdsRecursively = (
		node: LineageFlowNode | LineageFlowTemporaryNode,
		direction?: 'UPSTREAM' | 'DOWNSTREAM'
	): string[] => {
		if (!direction) {
			return [];
		}

		const getDirectlyImpactedNodes =
			direction === 'UPSTREAM' ? this.getIncomers : this.getOutgoers;

		const entityEdges = this.edges.filter(isEntityEdge);

		const stack = getDirectlyImpactedNodes(node, this.nodes, entityEdges);
		const impactedNodeIds = new Set(stack.map((n) => n.id));

		while (stack.length) {
			const currentNode = stack.pop();
			if (currentNode && isLineageFlowNode(currentNode)) {
				const nextNodes = getDirectlyImpactedNodes(
					currentNode,
					this.nodes,
					entityEdges
				);
				nextNodes.forEach((n) => {
					if (!impactedNodeIds.has(n.id)) {
						stack.push(n);
						impactedNodeIds.add(n.id);
					}
				});
			}
		}

		return Array.from(impactedNodeIds);
	};

	// ACTION HANDLER TO DELETE MANUAL LINEAGE
	onDeleteManualLineage = async ({ edgeId }: { edgeId: string }) => {
		const edge = this.edges.find((e) => e.id === edgeId);
		if (!edge || !edge.data) return;

		const { lineage_id } = edge.data;

		if (!lineage_id) return;

		await fetchApiDeleteLineage({
			pathParams: {
				lineageId: lineage_id,
			},
		});

		// Remove the edge first
		const sourceNode = this.nodes.find((n) => n.id === edge.source);
		const targetNode = this.nodes.find((n) => n.id === edge.target);

		let impactedNodeIds: string[] = [];

		if (!sourceNode || !targetNode) {
			// If nodes don't exist, just remove them
			const nodeIds = [edge.source, edge.target];
			runInAction(() => {
				this.edges = this.edges.filter((e) => e.id !== edgeId);
				this.nodes = this.nodes.filter((n) => !nodeIds.includes(n.id));
			});
		} else {
			const { nodeToDelete, direction } = this.determineNodeToDelete(
				sourceNode,
				targetNode
			);

			if (!nodeToDelete || !direction || !isLineageFlowNode(nodeToDelete)) {
				// If we can't determine what to delete, just remove the edge
				runInAction(() => {
					this.edges = this.edges.filter((e) => e.id !== edgeId);
				});
			} else {
				// Get all impacted nodes recursively
				const recursivelyImpactedIds = this.getDeleteImpactedNodeIdsRecursively(
					nodeToDelete,
					direction
				);
				impactedNodeIds = [nodeToDelete.id, ...recursivelyImpactedIds];

				runInAction(() => {
					this.edges = this.edges.filter((e) => e.id !== edgeId);
					this.nodes = this.nodes.filter(
						(n) => !impactedNodeIds.includes(n.id)
					);
					this.edges = this.edges.filter(
						(e) =>
							!impactedNodeIds.includes(e.source) &&
							!impactedNodeIds.includes(e.target)
					);
				});
			}
		}

		// Recalculate layout with the updated nodes and edges
		const { updatedGraph, updatedRankMap, updatedNodes } =
			await calculateLayout({
				nodes: this.nodes,
				edges: this.edges.filter(isEntityEdge),
			});

		runInAction(() => {
			this.updateGraphState({
				updatedGraph,
				updatedRankMap,
				updatedNodes: updatedNodes as (
					| LineageFlowNode
					| LineageFlowTemporaryNode
				)[],
			});
		});
	};

	// REACTFLOW ACTION HANDLERS
	onNodesChange = (
		changes: NodeChange<LineageFlowNode | LineageFlowTemporaryNode>[]
	) => {
		runInAction(() => {
			this.nodes = applyNodeChanges(changes, this.nodes) as (
				| LineageFlowNode
				| LineageFlowTemporaryNode
			)[];
		});
	};

	onEdgesChange = (changes: EdgeChange<LineageFlowEdge>[]) => {
		runInAction(() => {
			this.edges = applyEdgeChanges(changes, this.edges);
		});
	};

	onEdgeMouseEnter = (event: ReactMouseEvent, edge: LineageFlowEdge) => {
		this.updateEdgeHover(edge.id, true);
	};

	onEdgeMouseLeave = (event: ReactMouseEvent, edge: LineageFlowEdge) => {
		this.updateEdgeHover(edge.id, false);
	};
}

export default LineageFlowStore;
